/*
 * Shared tuning constants. Values here are provisional and tuned by playtesting.
 * Keeping them in one place makes each system independently testable (spec
 * sec. 11).
 */

const DEG = Math.PI / 180;

export const RENDER = {
  // Clamp per-frame delta so a stall (tab switch, breakpoint) can't produce a
  // huge simulation step once the loop resumes.
  maxFrameDeltaSeconds: 0.1,
  // Fixed step used for deterministic simulation (physics + player movement).
  fixedStepSeconds: 1 / 60,
  // Safety valve: max fixed sub-steps per rendered frame.
  maxFixedStepsPerFrame: 5,
};

export const CAMERA = {
  fov: 60,
  near: 0.15,
  far: 5000,
  chase: {
    // Default distance from the look-at point to the camera (mouse wheel
    // changes the live value between minZoom and maxZoom).
    distance: 5.4,
    minZoom: 2.6,
    maxZoom: 9.5,
    // Metres of distance change per wheel notch.
    zoomStep: 0.7,
    // Height of the look-at point above the player's feet.
    targetHeight: 1.5,
    // Pitch limits (radians above / below horizontal).
    minPitch: -55 * DEG,
    maxPitch: 72 * DEG,
    startPitch: 16 * DEG,
    // Base mouse look sensitivity (radians per pixel); scaled by the
    // user's look-sensitivity setting.
    sensitivity: 0.0024,
    // Exponential follow rate (higher = snappier).
    followLerp: 14,
    // Pull the camera in when an obstacle sits between it and the player, but
    // only while the camera is not aimed steeply groundward (see ChaseCamera).
    collisionMargin: 0.35,
    minDistance: 0.9,
    // Keep the camera at least this far above the ground instead of zooming in
    // when the shot gets low.
    groundClearance: 0.5,
    // While the ranged aim is held the camera pulls in over the shoulder.
    aimDistanceScale: 0.58,
    aimLerp: 12,
  },
};

// Consumed by Phase 2 (arena / kill height) and Phase 5 (death / reset).
export const WORLD = {
  killHeightY: -40,
  // Full square extent of the terrain heightfield, in metres.
  size: 440,
  // Grid resolution of the terrain (quads per side).
  segments: 168,
};

export const PLAYER = {
  maxHearts: 3,

  // After a hit the player cannot be damaged again for this long (spec sec. 6 —
  // "forgiving but meaningful"). The model blinks for the duration.
  invulnerabilitySeconds: 1.1,

  // Capsule collider: total height = 2*halfHeight + 2*radius ~= 1.7 m.
  capsuleHalfHeight: 0.5,
  capsuleRadius: 0.34,

  walkSpeed: 3.6,
  runSpeed: 7.6,
  // Horizontal acceleration toward the target velocity (m/s^2).
  groundAcceleration: 34,
  airAcceleration: 12,

  gravity: 24,
  maxFallSpeed: 55,
  jumpSpeed: 9.4,

  // Base rate at which the character turns to face the movement direction;
  // scaled by the user's turn-speed setting.
  turnResponsiveness: 17,

  glide: {
    // Steady descent rate once the glider is open (m/s).
    descentRate: 2.4,
    // How fast vertical speed eases to the descent rate when the glider opens.
    easing: 10,
    // Horizontal speed while gliding.
    speed: 6.6,
    // Minimum height above ground needed to deploy the glider (m).
    minHeight: 3.5,
  },

  // facing 0 = looking toward +Z (into the arena, away from the start camera).
  spawn: { x: 0, z: 0, facing: 0 },
};

/*
 * Rapier collision-interaction groups. Each collider is a member of one bit and
 * declares which bits it collides with; ray/shape queries filter the same way.
 * This keeps the enemy capsules out of the camera pull-in ray and lets enemies
 * pass through one another while still blocking the player.
 */
export const COLLISION = {
  WORLD: 0x0001,
  PLAYER: 0x0002,
  ENEMY: 0x0004,
};

/** Pack (memberships, filter) into one Rapier InteractionGroups u32. */
export function interactionGroups(memberships, filter) {
  return (((memberships & 0xffff) << 16) | (filter & 0xffff)) >>> 0;
}

export const GROUPS = {
  // Enemy capsule: collides with terrain/props and the player, not other enemies.
  enemyCollider: interactionGroups(
    COLLISION.ENEMY,
    COLLISION.WORLD | COLLISION.PLAYER,
  ),
  // Rays that should see only static world geometry (camera pull-in, ground
  // clearance, projectile impacts).
  rayWorld: interactionGroups(0xffff, COLLISION.WORLD),
  // Ranged aim ray: snaps to world geometry or an enemy, whichever is nearer.
  rayWorldOrEnemy: interactionGroups(
    0xffff,
    COLLISION.WORLD | COLLISION.ENEMY,
  ),
};

export const COMBAT = {
  melee: {
    // Whole swing length; the damage window is a slice of it. The hero's
    // `Attack` clip (astronaut-sword-animated.glb, authored 0.72 s) is
    // time-scaled by `PlayerModel` to fill exactly this long, so shortening
    // this value just makes the strike snappier (user request 2026-09-08).
    swingSeconds: 0.46,
    // Fraction of the swing during which a hit lands — over the clip's authored
    // forward-swing window (a touch wider each side = forgiving, spec §7).
    activeStart: 0.24,
    activeEnd: 0.62,
    cooldownSeconds: 0.1,
    // Horizontal reach from the player capsule centre (m).
    range: 2.4,
    // Half-angle of the frontal hit arc (radians).
    arc: 62 * DEG,
    // Vertical tolerance so a target on a slope still counts (m).
    verticalTolerance: 2.0,
    damage: 1,
  },
  ranged: {
    cooldownSeconds: 0.38,
    projectileSpeed: 60,
    // Metres travelled before the projectile gives up.
    maxRange: 120,
    maxLifeSeconds: 3,
    // A hit registers when the shot passes within this of an enemy chest (m).
    // Generous — the shot does not lead a moving target, and there is no
    // crosshair (spec §7, forgiving).
    hitRadius: 1.0,
    damage: 1,
    // Muzzle offset above the player's feet (m).
    muzzleHeight: 1.3,
    // Aim assist (spec sec. 7 — no crosshair, forgiving): if a goblin sits
    // within this cone of the camera forward and within range, the shot locks
    // onto its chest. cos(13 deg).
    assistConeCos: Math.cos(13 * DEG),
    assistRange: 90,
  },
  // Seconds a spark / impact puff stays alive.
  effectSeconds: 0.32,
};

export const ENEMY = {
  // Simultaneous living enemies stay in 6..11; start here (spec sec. 7).
  count: 8,
  minAlive: 6,
  maxAlive: 11,

  hearts: 3,
  // Standing height of the `orange-goblin.glb` model in-game (m) — "around the
  // same as the character" (user request); the hero stands 1.9 m. The glTF is
  // uniformly scaled to this and re-centred on the ground in `EnemyModel`.
  modelHeight: 1.85,
  // Capsule collider. Shorter than the model (the goblin is hunched, and the
  // 1.85 m includes horns / ears / crest) but tall enough to cover the torso so
  // the player cannot walk through it.
  capsuleHalfHeight: 0.45,
  capsuleRadius: 0.32,
  // Height of the chest above the feet — melee / ranged aim at this (m).
  chestHeight: 0.95,

  walkSpeed: 1.7,
  chaseSpeed: 4.3,
  turnResponsiveness: 9,

  // Perception / engagement radii, measured feet-to-feet (m).
  detectRange: 16,
  // Beyond this the enemy drops aggro and walks home.
  leashRange: 30,
  attackRange: 1.9,
  // The strike connects if the player is still within this when it lands.
  attackReach: 2.6,
  attackArc: 55 * DEG,

  // State timing (seconds).
  idleSeconds: [1.5, 4.0],
  patrolRadius: 9,
  // A slow, readable swing so the player can react (spec sec. 6 — forgiving).
  attackWindupSeconds: 0.45,
  attackStrikeSeconds: 0.12,
  attackRecoverySeconds: 1.4,
  hurtSeconds: 0.28,
  deathSeconds: 1.1,
  knockbackDistance: 0.4,

  damage: 1,
  respawnSeconds: 30,
  // A respawn is delayed while the home point sits inside the player's view
  // cone (within respawnHideDistance) or closer than respawnClearDistance to
  // the player (spec sec. 7).
  respawnHideConeCos: Math.cos(52 * DEG),
  respawnHideDistance: 46,
  respawnClearDistance: 9,
  respawnRetrySeconds: 3,
};

/*
 * Heart pickups (user request, post-Phase-6). Floating hearts spawn near the
 * player and refill one heart each. This is a deliberate, user-approved
 * deviation from spec sec. 2 ("no loot or collectible items") — see
 * docs/DECISIONS.md.
 */
export const PICKUP = {
  // Most hearts present in the world at once.
  maxAlive: 6,
  // First heart appears this many seconds into a run.
  firstSpawnSeconds: 5,
  // Random gap (seconds) between spawn attempts while below maxAlive.
  spawnIntervalSeconds: [6, 12],
  // Drop zone: an annulus around the player, so a heart is reachable but never
  // underfoot (metres, feet-to-feet).
  minDistance: 8,
  maxDistance: 22,
  // Collected when the player's feet come within this of the heart (metres).
  collectRadius: 1.6,
  // Hearts restored per pickup.
  healHearts: 1,
  // An uncollected heart fades and frees its slot after this long (seconds).
  lifetimeSeconds: 35,
  // Presentation.
  hoverHeight: 1.05,
  bobAmplitude: 0.16,
  bobSpeed: 2.1,
  spinSpeed: 1.7,
  popSeconds: 0.28,
};

/*
 * Killable chicken + goblin ambush (user request, 2026-09-04). A harmless
 * chicken placed through the level editor (`kind: "chicken"`, a `dynamic`
 * scenery asset). Killing one erupts a ring of relentless goblins at that spot.
 * A deliberate, user-approved deviation from spec sec. 2 / sec. 7 (it briefly
 * pushes the living-enemy count past 11) — see docs/DECISIONS.md.
 */
export const CHICKEN = {
  // One hit kills it (melee and ranged both deal 1).
  hearts: 1,
  // Aim point above the chicken's feet, for the melee cone / projectile sweep (m).
  chestHeight: 0.34,
  // Death animation length before the carcass is hidden (s).
  deathSeconds: 0.7,
  // Beat between the kill and the goblins erupting, so the feather puff reads (s).
  ambushDelaySeconds: 0.3,
  // Goblins spawned per chicken, ringed around its position.
  ambushCount: 6,
  ambushRadius: 2.6,
  // Safety valve for a map packed with chickens: total living goblins (the
  // fixed 8 + every ambush) never exceeds this.
  ambushMaxLiving: 20,
};

/*
 * Lore shrine (user request, post-deploy). A small open-air Greek-style temple
 * a short walk from the "simoncpu was here" signpost, with a glowing orb on a
 * stone plinth as its centrepiece. Walking in to the orb freezes play (like the
 * pause screen) and opens an "ancient scroll" overlay with the site owner's
 * bio, a looping video, and a guest-message box. A deliberate, user-approved
 * deviation from spec sec. 2 — see docs/DECISIONS.md.
 */
export const ALTAR = {
  // A short walk down the arena from SIGNS[0] ("simoncpu was here", at 3.5, 9)
  // and beyond it — squarely in the player's opening view (spawn is at 0,0
  // facing +Z), so it reads as a landmark the moment the game starts.
  position: { x: 8, z: 22 },
  // Fire when the player's feet come within this of the orb (XZ, metres).
  triggerRadius: 3.0,
  // Re-arm only after stepping back out past this — so closing the scroll while
  // still inside the temple does not immediately re-open it (hysteresis).
  rearmRadius: 4.8,
  // The shrine is a safe zone (user request): goblins cannot path within this
  // of the orb, and lose track of the player entirely while the player is
  // inside it. Covers the whole stylobate + the entrance stair, with margin.
  sanctuaryRadius: 9.5,

  // --- the orb + its plinth (local Y is measured from the temple floor) ---
  plinthRadius: 0.6,
  plinthHeight: 1.33,
  orbRadius: 0.4,
  orbHoverHeight: 1.9,
  orbBob: 0.12,
  orbBobSpeed: 1.6,
  orbSpinSpeed: 0.6,
  glowColor: 0x8fe1ff,
  lightIntensity: 8,
  lightRange: 20,

  // --- the temple (peristyle rectangle centred on the orb) ---------------
  temple: {
    halfX: 3.8, // column-ring half-extent across the front (metres)
    halfZ: 5.2, // column-ring half-extent down the flanks
    floorHeight: 0.35, // stylobate top above the sampled ground
    columnsX: 4, // columns along each short (front / back) side
    columnsZ: 5, // columns along each long side (corners shared)
    columnHeight: 3.7,
    columnRadius: 0.32,
    entablatureHeight: 0.62,
    pedimentRise: 1.0,
    marbleColor: 0xe9e3d3,
    stylobateColor: 0xd8d0bd,
  },
};

/*
 * Mushroom portal (user request, 2026-09-06). A giant Super-Mario-style red
 * spotted mushroom — taller than the lore temple — with a purple portal set into
 * the base of its stem. Walking into the portal navigates the browser away to
 * URL_ASTRAMARIO (another site / "dimension"). The mushroom itself is an
 * editor-placeable scenery asset (`src/world/assets/mushroom.js`); the portal's
 * glow, animation, and entry trigger are owned by `src/world/MushroomPortal.js`,
 * in the same mould as the lore Altar.
 */
export const PORTAL = {
  // Redirect target. Injected at build time from URL_ASTRAMARIO — `.env.dev`
  // under `npm run dev` (mode "dev"), `.env` under `npm run build` (mode
  // "production"). See vite.config.js `envPrefix`. Empty when unset (or under
  // `node --test`, where `import.meta.env` does not exist): every mushroom
  // portal then still glows but stays inert (you cannot fall through).
  url: import.meta.env?.URL_ASTRAMARIO ?? "",
  // Fire when the player's feet come within this of the portal plane (XZ, m).
  triggerRadius: 1.5,
  // Re-arm only after stepping back out past this (hysteresis — matters only for
  // the inert, no-URL case; a real entry navigates away).
  rearmRadius: 2.8,
  // `?spawn=mario` (a return trip from URL_ASTRAMARIO) drops the player this far
  // out in front of the portal — clear of triggerRadius so they don't loop
  // straight back, but the portal is right there to step into again.
  returnGap: 6,
  // Purple point light at each portal.
  glowColor: 0x9b3cff,
  lightIntensity: 6,
  lightRange: 14,
};

/*
 * Lore altar — the raised-fist monument (user request, 2026-09-07). An
 * editor-placeable, big solid low-poly fist (✊) that hovers and glows a short
 * walk from the "video games / don't cause violence" signs. Walking up to it
 * freezes play and opens a dedicated lore screen (scrolling text, optionally an
 * embedded video) with the background music faded out — the same overlay
 * behaviour as the temple `Altar`, minus the guest log. The shape + which
 * screen it opens are per-placement params; the glow, the hover, and the
 * proximity trigger are owned by `src/world/LoreAltar.js`, in the same mould as
 * `Altar` / `MushroomPortal`. A deliberate, user-approved deviation from spec
 * sec. 2 — see docs/DECISIONS.md.
 */
export const LORE_ALTAR = {
  // Fire when the player's feet come within this of the monument (XZ, metres).
  triggerRadius: 4.0,
  // Re-arm only after stepping back out past this (hysteresis — matches ALTAR).
  rearmRadius: 6.0,
  // Safe zone (user request): goblins keep out, and no goblin / ambush goblin /
  // heart pickup ever spawns inside. Covers the monument + a courtyard margin.
  sanctuaryRadius: 11,

  // --- presentation ----------------------------------------------------
  // Height of the monument's centre above the sampled ground (metres) — big
  // enough that a `scale` ~5 m fist floats with its wrist clear of the ground.
  // The shape's own height is the placement's `scale` param (default 5.2 m).
  hoverHeight: 4.2,
  bob: 0.18,
  bobSpeed: 1.1,
  // Gentle yaw sway either side of the placed facing (radians) — a full spin
  // would hide the fist's front (it faces the visitor).
  sway: 0.14,
  swaySpeed: 0.45,
  glowColor: 0x53d6ec,
  lightIntensity: 9,
  lightRange: 26,
  // Swirling energy wreathed around the fist (LoreAltar.js): counter-spinning
  // orbit rings plus a scrolling spiral shell, all additively blended.
  energyColor: 0x8ff2ff,
  // Fraction of the fist's peak-to-peak scale the whole monument breathes by.
  breathe: 0.03,
};

/*
 * Crashed UFO — a third lore-altar variant (user request, 2026-09-09; the
 * `.glb` landed 2026-09-08). A saucer resting crash-landed on the plateau:
 * walking up to it freezes play, fades the music out, and opens a text-only
 * `#screen-lore-<screen>` parchment — the raised-fist lore altar's behaviour
 * (`GameState.LORE`) minus the hover and the swirling energy. It keeps the
 * supplied `.glb`'s own look and its baked `Crash_Idle` lamp-flicker animation.
 * Owned by `src/world/CrashedUfoAltar.js`. A deliberate, user-approved
 * deviation from spec sec. 2 — see docs/DECISIONS.md.
 */
export const CRASHED_UFO = {
  // Fire when the player's feet come within this of the saucer centre (XZ, m),
  // at placement scale 1 — CrashedUfoAltar.fixedUpdate() multiplies this (and
  // rearmRadius) by each wreck's own `scale` param, so a resized wreck keeps
  // a trigger circle outside its own (also scaled) hull collider.
  // The rim is ~5 m out at scale 1; this fires as they walk up to the wreck.
  triggerRadius: 6.5,
  // Re-arm only after stepping back out past this (hysteresis — matches ALTAR).
  rearmRadius: 9.0,
  // Safe zone (matches the fist altar): goblins keep out, nothing spawns
  // inside, and a player beside the wreck is hidden from goblins.
  sanctuaryRadius: 10,
};

export const STORAGE = {
  // Persisted user settings (spec sec. 10). The audio preference is written by
  // the AudioManager under its own key; comfort settings (mouse invert, look
  // sensitivity, turn speed) live together as JSON under settingsKey.
  audioPrefKey: "mtap.audioEnabled",
  settingsKey: "mtap.settings",
};

// User-adjustable comfort settings. These are camera / feel options, not key
// remapping (which stays excluded, spec sec. 2). Defaults live here; the store
// is src/core/Settings.js and the interim UI is on the pause screen.
export const SETTINGS_DEFAULTS = {
  invertLookY: false,
  // Multipliers on CAMERA.chase.sensitivity and PLAYER.turnResponsiveness.
  lookSensitivity: 1,
  turnSpeed: 1,
};

export const SETTINGS_RANGES = {
  lookSensitivity: { min: 0.3, max: 2.5, step: 0.05 },
  turnSpeed: { min: 0.5, max: 2.5, step: 0.05 },
};
